import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid,
  TextField, FormControlLabel, Switch, MenuItem, Alert, Box, Typography,
} from '@mui/material';
import patientsService from '../../services/patientsService';

/**
 * Schema-driven capture dialog for the expediente. One component covers all
 * six data types; each `kind` declares its fields and which service call
 * persists it. Real writes — no mock. On success the parent re-fetches the
 * expediente so the new data shows immediately.
 *
 * Field types: text | number | bool | date | time | select | chips
 * (chips = comma-separated text → array of strings).
 */
const SCHEMAS = {
  anthropometrics: {
    title: 'Nueva medición antropométrica',
    save: (id, data) => patientsService.addAnthropometrics(id, data),
    update: (id, recordId, data) => patientsService.updateAnthropometrics(id, recordId, data),
    fromRecord: (r) => ({
      measurement_date: r.fecha_medicion ? String(r.fecha_medicion).slice(0, 10) : '',
      weight_kg: r.peso_kg,
      height_cm: r.talla_cm,
      waist_cm: r.circunferencia_cintura_cm,
      hip_cm: r.circunferencia_cadera_cm,
      arm_cm: r.circunferencia_brazo_cm,
      body_fat_pct: r.porcentaje_grasa,
      muscle_mass_kg: r.masa_muscular_kg,
      notes: r.notas,
    }),
    fields: [
      { name: 'measurement_date', label: 'Fecha de medición', type: 'date' },
      { name: 'weight_kg', label: 'Peso (kg)', type: 'number', required: true },
      { name: 'height_cm', label: 'Talla (cm)', type: 'number' },
      { name: 'waist_cm', label: 'Circunferencia cintura (cm)', type: 'number' },
      { name: 'hip_cm', label: 'Circunferencia cadera (cm)', type: 'number' },
      { name: 'arm_cm', label: 'Circunferencia brazo (cm)', type: 'number' },
      { name: 'body_fat_pct', label: '% Grasa corporal', type: 'number' },
      { name: 'muscle_mass_kg', label: 'Masa muscular (kg)', type: 'number' },
      { name: 'notes', label: 'Notas', type: 'text', multiline: true },
    ],
  },
  vital_signs: {
    title: 'Registrar signos vitales',
    save: (id, data) => patientsService.addVitalSigns(id, data),
    update: (id, recordId, data) => patientsService.updateVitalSigns(id, recordId, data),
    fromRecord: (r) => ({
      systolic_mmhg: r.presion_sistolica_mmHg,
      diastolic_mmhg: r.presion_diastolica_mmHg,
      heart_rate_bpm: r.frecuencia_cardiaca_lpm,
      respiratory_rate_rpm: r.frecuencia_respiratoria_rpm,
      temperature_celsius: r.temperatura_celsius,
      oxygen_saturation_pct: r.saturacion_oxigeno_porcentaje,
      capillary_glucose_mgdl: r.glucosa_capilar_mgdl,
      glucose_timing: r.momento_medicion,
      notes: r.notas,
    }),
    fields: [
      { name: 'systolic_mmhg', label: 'Presión sistólica (mmHg)', type: 'number' },
      { name: 'diastolic_mmhg', label: 'Presión diastólica (mmHg)', type: 'number' },
      { name: 'heart_rate_bpm', label: 'Frecuencia cardíaca (lpm)', type: 'number' },
      { name: 'respiratory_rate_rpm', label: 'Frecuencia respiratoria (rpm)', type: 'number' },
      { name: 'temperature_celsius', label: 'Temperatura (°C)', type: 'number' },
      { name: 'oxygen_saturation_pct', label: 'Saturación O₂ (%)', type: 'number' },
      { name: 'capillary_glucose_mgdl', label: 'Glucosa capilar (mg/dL)', type: 'number' },
      { name: 'glucose_timing', label: 'Momento glucosa', type: 'select',
        options: [['ayuno', 'En ayuno'], ['postprandial', 'Postprandial'], ['casual', 'Casual']] },
      { name: 'notes', label: 'Notas', type: 'text', multiline: true },
    ],
  },
  medical_history: {
    title: 'Editar historia clínica',
    save: (id, data) => patientsService.saveMedicalHistory(id, data),
    fields: [
      { name: 'conditions', label: 'Enfermedades crónicas (separadas por coma)', type: 'chips' },
      { name: 'allergies', label: 'Alergias a alimentos (coma)', type: 'chips' },
      { name: 'intolerances', label: 'Intolerancias (coma)', type: 'chips' },
      { name: 'medications', label: 'Medicamentos actuales (coma)', type: 'chips' },
      { name: 'smoking', label: 'Fuma', type: 'bool' },
      { name: 'alcohol_consumption', label: 'Consumo de alcohol', type: 'select',
        options: [['none', 'Ninguno'], ['occasional', 'Ocasional'], ['moderate', 'Moderado'], ['high', 'Alto']] },
      { name: 'sleep_hours', label: 'Horas de sueño', type: 'number' },
      { name: 'stress_level', label: 'Nivel de estrés (1-10)', type: 'number' },
      { name: 'emergency_contact_name', label: 'Contacto de emergencia', type: 'text' },
      { name: 'emergency_contact_phone', label: 'Teléfono de emergencia', type: 'text' },
      { name: 'emergency_contact_relationship', label: 'Parentesco', type: 'text' },
    ],
  },
  laboratory: {
    title: 'Registrar resultados de laboratorio',
    save: (id, data) => patientsService.addLaboratory(id, data),
    fields: [
      { name: 'study_date', label: 'Fecha del estudio', type: 'date', required: true },
      { name: 'laboratory_name', label: 'Laboratorio', type: 'text' },
      { name: 'fasting_glucose_mgdl', label: 'Glucosa ayuno (mg/dL)', type: 'number' },
      { name: 'hemoglobin_a1c_pct', label: 'Hemoglobina A1c (%)', type: 'number' },
      { name: 'total_cholesterol_mgdl', label: 'Colesterol total (mg/dL)', type: 'number' },
      { name: 'ldl_cholesterol_mgdl', label: 'LDL (mg/dL)', type: 'number' },
      { name: 'hdl_cholesterol_mgdl', label: 'HDL (mg/dL)', type: 'number' },
      { name: 'triglycerides_mgdl', label: 'Triglicéridos (mg/dL)', type: 'number' },
      { name: 'creatinine_mgdl', label: 'Creatinina (mg/dL)', type: 'number' },
      { name: 'alt_tgp_UI_l', label: 'ALT/TGP (UI/L)', type: 'number' },
      { name: 'hemoglobin_g_dl', label: 'Hemoglobina (g/dL)', type: 'number' },
      { name: 'vitamin_d_ng_ml', label: 'Vitamina D (ng/mL)', type: 'number' },
      { name: 'notes', label: 'Notas', type: 'text', multiline: true },
    ],
  },
  habits: {
    title: 'Registrar hábitos',
    save: (id, data) => patientsService.saveHabits(id, data),
    fields: [
      { name: 'meals_per_day', label: 'Comidas al día', type: 'number' },
      { name: 'eats_breakfast_daily', label: 'Desayuna a diario', type: 'bool' },
      { name: 'water_glasses_day', label: 'Vasos de agua al día', type: 'number' },
      { name: 'drinks_soda', label: 'Consume refrescos', type: 'bool' },
      { name: 'sodas_per_week', label: 'Refrescos por semana', type: 'number' },
      { name: 'drinks_alcohol', label: 'Consume alcohol', type: 'bool' },
      { name: 'smokes', label: 'Fuma', type: 'bool' },
      { name: 'eats_watching_tv', label: 'Come viendo TV', type: 'bool' },
      { name: 'eats_fast', label: 'Come rápido', type: 'bool' },
      { name: 'favorite_foods', label: 'Alimentos favoritos (coma)', type: 'chips' },
      { name: 'rejected_foods', label: 'Alimentos rechazados (coma)', type: 'chips' },
      { name: 'notes', label: 'Notas', type: 'text', multiline: true },
    ],
  },
  physical_activity: {
    title: 'Registrar actividad física',
    save: (id, data) => patientsService.savePhysicalActivity(id, data),
    fields: [
      { name: 'activity_level', label: 'Nivel de actividad', type: 'select',
        options: [['sedentario', 'Sedentario'], ['ligero', 'Ligero'], ['moderado', 'Moderado'], ['intenso', 'Intenso']] },
      { name: 'work_type', label: 'Tipo de trabajo', type: 'text' },
      { name: 'sitting_hours_day', label: 'Horas sentado/día', type: 'number' },
      { name: 'does_exercise', label: 'Realiza ejercicio', type: 'bool' },
      { name: 'exercise_types', label: 'Tipos de ejercicio (coma)', type: 'chips' },
      { name: 'weekly_frequency', label: 'Frecuencia semanal', type: 'number' },
      { name: 'avg_duration_min', label: 'Duración promedio (min)', type: 'number' },
      { name: 'daily_walk_min', label: 'Camina al día (min)', type: 'number' },
      { name: 'avg_steps_day', label: 'Pasos promedio/día', type: 'number' },
      { name: 'steps_goal_day', label: 'Meta de pasos/día', type: 'number' },
      { name: 'pain_during_exercise', label: 'Dolor al ejercitarse', type: 'bool' },
      { name: 'notes', label: 'Notas', type: 'text', multiline: true },
    ],
  },
};

const CapturaExpedienteDialog = ({ open, kind, patientId, record, onClose, onSaved }) => {
  const schema = kind ? SCHEMAS[kind] : null;
  const isEdit = Boolean(record && record.id);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset (create) or pre-fill (edit) whenever the dialog opens.
  React.useEffect(() => {
    if (!open) return;
    setError('');
    if (record && record.id && schema && schema.fromRecord) {
      const pre = schema.fromRecord(record);
      Object.keys(pre).forEach((k) => { if (pre[k] === null || pre[k] === undefined) delete pre[k]; });
      setValues(pre);
    } else {
      setValues({});
    }
  }, [open, kind, record, schema]);

  if (!schema) return null;

  const setField = (name, value) => setValues((v) => ({ ...v, [name]: value }));

  const buildPayload = () => {
    const out = {};
    for (const f of schema.fields) {
      const raw = values[f.name];
      if (f.type === 'bool') {
        if (raw !== undefined) out[f.name] = !!raw;
      } else if (raw === undefined || raw === '') {
        continue;
      } else if (f.type === 'number') {
        const n = Number(raw);
        if (!Number.isNaN(n)) out[f.name] = n;
      } else if (f.type === 'chips') {
        const arr = String(raw).split(',').map((s) => s.trim()).filter(Boolean);
        if (arr.length) out[f.name] = arr;
      } else {
        out[f.name] = raw;
      }
    }
    return out;
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      const required = schema.fields.filter((f) => f.required && (payload[f.name] === undefined));
      if (required.length) {
        setError(`Falta: ${required.map((f) => f.label).join(', ')}`);
        setSaving(false);
        return;
      }
      if (isEdit && schema.update) {
        await schema.update(patientId, record.id, payload);
      } else {
        await schema.save(patientId, payload);
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'No se pudo guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{isEdit ? schema.title.replace(/^(Nueva|Registrar|Editar)/, 'Editar') : schema.title}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Captura sólo lo que tengas. Los campos vacíos se omiten.
        </Typography>
        <Grid container spacing={2}>
          {schema.fields.map((f) => (
            <Grid item xs={12} sm={f.multiline ? 12 : 6} key={f.name}>
              {f.type === 'bool' ? (
                <FormControlLabel
                  control={<Switch checked={!!values[f.name]} onChange={(e) => setField(f.name, e.target.checked)} />}
                  label={f.label}
                />
              ) : f.type === 'select' ? (
                <TextField
                  select fullWidth label={f.label} value={values[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                >
                  {f.options.map(([val, lbl]) => (
                    <MenuItem key={val} value={val}>{lbl}</MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  label={f.label + (f.required ? ' *' : '')}
                  type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                  multiline={!!f.multiline}
                  minRows={f.multiline ? 2 : undefined}
                  InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                  value={values[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                />
              )}
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { SCHEMAS };
export default CapturaExpedienteDialog;
